import React, { useEffect } from "react";

import { Lazy } from "../../component/Lazy";
import { Redirect } from "../../component/Redirect";
import { ViewSmartFleetEvents } from "../../component/ViewSmartFleetEvents/ViewSmartFleetEvents";
import { useStores } from "../../context/StoreContext";
import { IRoutes } from "../IRoutes";
import { path } from "../path";
import { Route } from "../Route";
import { DefaultRoute, RestrictedRoute } from "../RouteHelpers";
import { uuid } from "../routeParameters";
import { deviceRoutes } from "./deviceRoutes";

const Benchmark = Lazy(
    async () => (await import("../../component/Benchmark")).Benchmark,
    { displayName: "Benchmark" },
);

const ChannelById = Lazy(
    async () => (await import("../../component/ChannelById")).ChannelById,
    { displayName: "ChannelById" },
);

const ChannelByQuery = Lazy(
    async () => (await import("../../component/ChannelByQuery")).ChannelByQuery,
    { displayName: "ChannelByQuery" },
);

const ChannelEvent = Lazy(
    async () => (await import("../../component/ChannelEvent")).ChannelEvent,
    { displayName: "ChannelEvent" },
);

const CloneDeviceConfigurationTemplate = Lazy(
    async () =>
        (await import("../../component/CloneDeviceConfigurationTemplate"))
            .CloneDeviceConfigurationTemplate,
    { displayName: "CloneDeviceConfigurationTemplate" },
);

const CloneView = Lazy(
    async () => (await import("../../component/CloneView")).CloneView,
    { displayName: "CloneView" },
);

const CreateAdapter = Lazy(
    async () => (await import("../../component/CreateAdapter")).CreateAdapter,
    { displayName: "CreateAdapter" },
);

const CreateAnnotationTemplate = Lazy(
    async () =>
        (await import("../../component/CreateAnnotationTemplate"))
            .CreateAnnotationTemplate,
    { displayName: "CreateAnnotationTemplate" },
);

const CreateChannel = Lazy(
    async () => (await import("../../component/CreateChannel")).CreateChannel,
    { displayName: "CreateChannel" },
);

const CreateCommandTemplate = Lazy(
    async () =>
        (await import("../../component/CreateCommandTemplate"))
            .CreateCommandTemplate,
    { displayName: "CreateCommandTemplate" },
);

const CreateDeviceConfigurationTemplate = Lazy(
    async () =>
        (await import("../../component/CreateDeviceConfigurationTemplate"))
            .CreateDeviceConfigurationTemplate,
    { displayName: "CreateDeviceConfigurationTemplate" },
);

const CreateEventTrigger = Lazy(
    async () =>
        (await import("../../component/CreateEventTrigger")).CreateEventTrigger,
    { displayName: "CreateEventTrigger" },
);

const CreateGroup = Lazy(
    async () => (await import("../../component/CreateGroup")).CreateGroup,
    { displayName: "CreateGroup" },
);

const CreateModule = Lazy(
    async () => (await import("../../component/CreateModule")).CreateModule,
    { displayName: "CreateModule" },
);

const CreateRole = Lazy(
    async () => (await import("../../component/CreateRole")).CreateRole,
    { displayName: "CreateRole" },
);

const CreateSchedule = Lazy(
    async () => (await import("../../component/CreateSchedule")).CreateSchedule,
    { displayName: "CreateSchedule" },
);

const CreateServiceAccount = Lazy(
    async () =>
        (await import("../../component/CreateServiceAccount"))
            .CreateServiceAccount,
    { displayName: "CreateServiceAccount" },
);

const CreatePersona = Lazy(
    async () => (await import("../../component/CreatePersona")).CreatePersona,
    { displayName: "CreatePersona" },
);

const CreateStream = Lazy(
    async () => (await import("../../component/CreateStream")).CreateStream,
    { displayName: "CreateStream" },
);

const CreateTagTemplate = Lazy(
    async () =>
        (await import("../../component/CreateTagTemplate")).CreateTagTemplate,
    { displayName: "CreateTagTemplate" },
);

const CreateTeam = Lazy(
    async () => (await import("../../component/CreateTeam")).CreateTeam,
    { displayName: "CreateTeam" },
);

const CreateUser = Lazy(
    async () => (await import("../../component/CreateUser")).CreateUser,
    { displayName: "CreateUser" },
);

const CreateView = Lazy(
    async () => (await import("../../component/CreateView")).CreateView,
    { displayName: "CreateView" },
);

const Debug = Lazy(async () => (await import("../../component/Debug")).Debug, {
    displayName: "Debug",
});

const EditAdapterById = Lazy(
    async () =>
        (await import("../../component/EditAdapterById")).EditAdapterById,
    { displayName: "EditAdapterById" },
);

const EditAnnotationTemplateById = Lazy(
    async () =>
        (await import("../../component/EditAnnotationTemplateById"))
            .EditAnnotationTemplateById,
    { displayName: "EditAnnotationTemplateById" },
);

const EditChannelById = Lazy(
    async () =>
        (await import("../../component/EditChannelById")).EditChannelById,
    { displayName: "EditChannelById" },
);

const EditCommandTemplateById = Lazy(
    async () =>
        (await import("../../component/EditCommandTemplateById"))
            .EditCommandTemplateById,
    { displayName: "EditCommandTemplateById" },
);

const EditDevice = Lazy(
    async () => (await import("../../component/EditDevice")).EditDevice,
    { displayName: "EditDevice" },
);

const EditDeviceConfigurationTemplateById = Lazy(
    async () =>
        (await import("../../component/EditDeviceConfigurationTemplateById"))
            .EditDeviceConfigurationTemplateById,
    { displayName: "EditDeviceConfigurationTemplateById" },
);

const EditEventTriggerById = Lazy(
    async () =>
        (await import("../../component/EditEventTriggerById"))
            .EditEventTriggerById,
    { displayName: "EditEventTriggerById" },
);

const EditGroupById = Lazy(
    async () => (await import("../../component/EditGroupById")).EditGroupById,
    { displayName: "EditGroupById" },
);

const EditModuleById = Lazy(
    async () => (await import("../../component/EditModuleById")).EditModuleById,
    { displayName: "EditModuleById" },
);

const EditStreamById = Lazy(
    async () => (await import("../../component/EditStreamById")).EditStreamById,
    { displayName: "EditStreamById" },
);

const EditTagTemplateById = Lazy(
    async () =>
        (await import("../../component/EditTagTemplateById"))
            .EditTagTemplateById,
    { displayName: "EditTagTemplateById" },
);

const EditTeamById = Lazy(
    async () => (await import("../../component/EditTeamById")).EditTeamById,
    { displayName: "EditTeamById" },
);

const EditUserById = Lazy(
    async () => (await import("../../component/EditUserById")).EditUserById,
    { displayName: "EditUserById" },
);

const EditViewById = Lazy(
    async () => (await import("../../component/EditViewById")).EditViewById,
    { displayName: "EditViewById" },
);

const Events = Lazy(
    async () => (await import("../../component/Events")).Events,
    {
        displayName: "Events",
    },
);

const ExportDeviceConfigurationTemplate = Lazy(
    async () =>
        (await import("../../component/ExportDeviceConfigurationTemplate"))
            .ExportDeviceConfigurationTemplate,
    { displayName: "ExportDeviceConfigurationTemplate" },
);

const ForgotPassword = Lazy(
    async () => (await import("../../component/ForgotPassword")).ForgotPassword,
    { displayName: "ForgotPassword" },
);

const SSOLogin = Lazy(
    async () => (await import("../../component/SSOLogin")).SSOLogin,
    { displayName: "SSOLogin" },
);

const GoogleIntegration = Lazy(
    async () =>
        (await import("../../component/GoogleIntegration")).GoogleIntegration,
    { displayName: "GoogleIntegration" },
);

const HomePage = Lazy(
    async () => (await import("../../component/HomePage")).HomePage,
    { displayName: "HomePage" },
);

const Impersonate = Lazy(
    async () => (await import("../../component/Impersonate")).Impersonate,
    { displayName: "Impersonate" },
);

const Internal = Lazy(
    async () => (await import("../../component/Internal")).Internal,
    { displayName: "Internal" },
);

const AdminPanel = Lazy(
    async () =>
        (await import("../../component/Internal/AdminPanel/AdminPanel"))
            .AdminPanel,
    { displayName: "AdminPanel" },
);

const Organization = Lazy(
    async () =>
        (await import("../../component/Internal/Organizations/Organization"))
            .Organization,
    { displayName: "Organization" },
);

const UiDemo = Lazy(
    async () => (await import("../../component/UiDemo/index")).UiDemo,
    { displayName: "SignUp" },
);

const LoginRedirect = Lazy(
    async () => (await import("../../component/LoginRedirect")).LoginRedirect,
    { displayName: "LoginRedirect" },
);

const Marketplace = Lazy(
    async () => (await import("../../component/Marketplace")).Marketplace,
    { displayName: "Marketplace" },
);

const Operate = Lazy(
    async () => (await import("../../component/Operate")).Operate,
    { displayName: "Operate" },
);

const PagerdutyIntegration = Lazy(
    async () =>
        (await import("../../component/PagerdutyIntegration"))
            .PagerdutyIntegration,
    { displayName: "PagerdutyIntegration" },
);

const PrivacyPolicy = Lazy(
    async () => (await import("../../component/PrivacyPolicy")).PrivacyPolicy,
    { displayName: "PrivacyPolicy" },
);

const Profile = Lazy(
    async () => (await import("../../component/Profile")).Profile,
    { displayName: "Profile" },
);

const ResendInvitation = Lazy(
    async () =>
        (await import("../../component/ResendInvitation")).ResendInvitation,
    { displayName: "ResendInvitation" },
);

const Settings = Lazy(
    async () => (await import("../../component/Settings")).Settings,
    { displayName: "Settings" },
);

const BulkProvision = Lazy(
    async () =>
        (await import("../../component/Settings/BulkProvision")).BulkProvision,
    { displayName: "BulkProvision" },
);

const EditRoleById = Lazy(
    async () =>
        (await import("../../component/Settings/EditRoleById")).EditRoleById,
    { displayName: "EditRoleById" },
);

const EditScheduleById = Lazy(
    async () =>
        (await import("../../component/Settings/EditScheduleByid"))
            .EditScheduleById,
    { displayName: "EditScheduleById" },
);

const CloneRole = Lazy(
    async () =>
        (await import("../../component/Settings/Roles/CloneRole")).CloneRole,
    { displayName: "CloneRole" },
);

const ShareByCode = Lazy(
    async () => (await import("../../component/ShareByCode")).ShareByCode,
    { displayName: "ShareByCode" },
);

const ShareTeleopByCode = Lazy(
    async () =>
        (await import("../../component/ShareTeleopByCode")).ShareTeleopByCode,
    { displayName: "ShareTeleopByCode" },
);

const SlackIntegration = Lazy(
    async () =>
        (await import("../../component/SlackIntegration")).SlackIntegration,
    { displayName: "SlackIntegration" },
);

const Terms = Lazy(async () => (await import("../../component/Terms")).Terms, {
    displayName: "Terms",
});

const Utilities = Lazy(
    async () => (await import("../../component/Utilities")).Utilities,
    { displayName: "Utilities" },
);

const ViewEvent = Lazy(
    async () => (await import("../../component/ViewEvent")).ViewEvent,
    { displayName: "ViewEvent" },
);

const ConnectedViewSmartFleet = Lazy(
    async () =>
        (await import("../../component/ViewSmartFleet/ConnectedViewSmartFleet"))
            .ConnectedViewSmartFleet,
    { displayName: "ConnectedViewSmartFleet" },
);

const SmartFleetById = Lazy(
    async () =>
        (await import("../../component/ViewSmartFleet/SmartFleetById"))
            .SmartFleetById,
    { displayName: "SmartFleetById" },
);

export const routes: IRoutes = {
    ui: DefaultRoute("/ui", () => <UiDemo />),
    login: new Route("/login", () => <LoginRedirect />),
    impersonate: new Route<{ userId: string }>(
        "/impersonate/:userId",
        ({ userId }) => <Impersonate userId={userId} />,
    ),
    terms: new Route("/terms", () => <Terms />),
    privacyPolicy: new Route("/privacy-policy", () => <PrivacyPolicy />),
    ssoLogin: new Route("/sso-login", () => <SSOLogin />),
    forgotPassword: new Route("/forgot-password", () => <ForgotPassword />),
    resendInvitation: new Route("/resend-invitation", () => (
        <ResendInvitation />
    )),
    share: new Route<{ code: string }>("/shares/:code", ({ code }) => (
        <ShareByCode code={code} />
    )),
    preview: new Route<{ code: string }>("/preview/:code", ({ code }) => {
        const { appStore } = useStores();
        useEffect(() => {
            appStore.preview = true;
        }, [appStore]);
        return <ShareByCode code={code} />;
    }),
    shareTeleop: new Route<{ deviceId: string; code: string }>(
        "/share-teleop/:deviceId/:code",
        ({ deviceId, code }) => (
            <ShareTeleopByCode code={code} deviceId={deviceId} />
        ),
    ),
    previewTeleop: new Route<{ deviceId: string; code: string }>(
        "/preview-teleop/:deviceId/:code",
        ({ deviceId, code }) => {
            const { appStore } = useStores();
            useEffect(() => {
                appStore.preview = true;
            }, [appStore]);
            return <ShareTeleopByCode code={code} deviceId={deviceId} />;
        },
    ),
    internal: DefaultRoute("/internal", () => <Internal />),
    internalOrganization: DefaultRoute<{ id: string }>(
        path`/internal/${uuid(":id")}`,
        Organization,
    ),
    internalAdminPanel: DefaultRoute("/admin-panel", () => <AdminPanel />),
    home: DefaultRoute("/", () => <HomePage />),
    settings: DefaultRoute("/settings", () => <Settings />),
    applications: DefaultRoute("/applications", () => <Marketplace />),
    profile: DefaultRoute("/profile", () => <Profile />),
    debug: DefaultRoute("/debug", () => <Debug />),
    slackIntegration: RestrictedRoute(
        "/integrations/slack",
        () => <SlackIntegration />,
        "integrations",
        "administrator",
        "advancedConfiguration",
    ),
    googleIntegration: RestrictedRoute(
        "/integrations/google",
        () => <GoogleIntegration />,
        "integrations",
        "administrator",
        "advancedConfiguration",
    ),
    pagerdutyIntegration: RestrictedRoute(
        "/integrations/pagerduty",
        () => <PagerdutyIntegration />,
        "integrations",
        "administrator",
        "advancedConfiguration",
    ),
    utilities: RestrictedRoute(
        "/utilities",
        () => <Utilities />,
        "teleop",
        "operator",
    ),
    channelByQuery: DefaultRoute(
        "/channel",
        () => <ChannelByQuery />,
        "observability",
    ),
    events: DefaultRoute("/events", () => <Events />, "events"),
    event: DefaultRoute<{ id: string }>(
        path`/events/${uuid(":id")}`,
        ViewEvent,
        "events",
    ),
    // TODO: Remove (backwards-compatibility)
    notification: DefaultRoute<{ id: string }>(
        "/notifications/:id",
        ({ id }) => <Redirect to={`/events/${id}`} />,
        "events",
    ),
    benchmark: DefaultRoute("/benchmark", () => <Benchmark />),

    createEventTrigger: RestrictedRoute(
        "/create-event-trigger",
        () => <CreateEventTrigger />,
        "events",
        "administrator",
    ),
    editEventTrigger: RestrictedRoute<{ id: string }>(
        path`/triggered-events/${uuid(":id")}`,
        EditEventTriggerById,
        "events",
        "administrator",
    ),

    createDeviceConfigurationTemplate: RestrictedRoute(
        "/create-template",
        () => <CreateDeviceConfigurationTemplate />,
        "devices",
        "administrator",
    ),
    exportDeviceConfigurationTemplate: RestrictedRoute<{ id: string }>(
        "/templates/:id/export",
        ({ id }) => <ExportDeviceConfigurationTemplate deviceId={id} />,
        "devices",
        "administrator",
    ),
    cloneDeviceConfigurationTemplate: RestrictedRoute<{ id: string }>(
        "/templates/:id/clone",
        ({ id }) => <CloneDeviceConfigurationTemplate id={id} />,
        "devices",
        "administrator",
    ),
    editDeviceConfigurationTemplate: RestrictedRoute<{ id: string }>(
        path`/templates/${uuid(":id")}`,
        EditDeviceConfigurationTemplateById,
        "devices",
        "administrator",
    ),
    ...deviceRoutes,
    smartFleet: DefaultRoute<{ id: string }>(
        path`/smart-fleet/${uuid(":id")}`,
        ({ id }) => <SmartFleetById key={id} id={id} />,
    ),
    smartFleetWithView: DefaultRoute<{ id: string; viewId: string }>(
        path`/smart-fleet/${uuid(":id")}/views/${uuid(":viewId")}`,
        ({ id, viewId }) => (
            <ConnectedViewSmartFleet
                key={`${id}:${viewId}`}
                id={id}
                viewId={viewId}
            />
        ),
    ),
    smartFleetEventsWithView: DefaultRoute<{ id: string }>(
        path`/smart-fleet/${uuid(":id")}/events`,
        ({ id }) => <ViewSmartFleetEvents key={id} id={id} />,
    ),
    editDevice: RestrictedRoute<{ id: string }>(
        path`/devices/${uuid(":id")}/edit`,
        EditDevice,
        "devices",
        "edit-tags",
    ),
    editDeviceAddStream: RestrictedRoute<{ id: string }>(
        path`/devices/${uuid(":id")}/edit/add-stream`,
        ({ id }) => <EditDevice id={id} addStream={true} />,
        "devices",
        "edit-tags",
    ),
    teleop: RestrictedRoute<{ id: string }>(
        path`/devices/${uuid(":id")}/teleop`,
        ({ id }) => <Operate key={id} id={id} onboarding={false} />,
        "teleop",
        "operator",
    ),
    teleopOnboarding: RestrictedRoute<{ id: string }>(
        path`/devices/${uuid(":id")}/teleop-onboarding`,
        ({ id }) => <Operate key={id} id={id} onboarding={true} />,
        "teleop",
        "operator",
    ),
    createView: RestrictedRoute(
        "/create-view",
        () => <CreateView />,
        "views",
        "administrator",
    ),
    cloneView: RestrictedRoute<{ id: string }>(
        path`/views/${uuid(":id")}/clone`,
        CloneView,
        "views",
        "administrator",
    ),
    editView: RestrictedRoute<{ id: string }>(
        path`/views/${uuid(":id")}`,
        EditViewById,
        "views",
        "administrator",
    ),
    createCommandTemplate: RestrictedRoute(
        "/create-command",
        () => <CreateCommandTemplate />,
        "commands",
        "administrator",
    ),
    editCommandTemplate: RestrictedRoute<{ id: string }>(
        path`/commands/${uuid(":id")}`,
        EditCommandTemplateById,
        "commands",
        "administrator",
    ),
    createAnnotationTemplate: RestrictedRoute(
        "/create-annotation",
        () => <CreateAnnotationTemplate />,
        "annotations",
        "administrator",
    ),
    editAnnotationTemplate: RestrictedRoute<{ id: string }>(
        path`/annotations/${uuid(":id")}`,
        EditAnnotationTemplateById,
        "annotations",
        "administrator",
    ),
    createUser: RestrictedRoute(
        "/create-user",
        () => <CreateUser />,
        "users",
        "administrator",
    ),
    createTeam: RestrictedRoute(
        "/create-team",
        () => <CreateTeam />,
        "users",
        "administrator",
    ),
    createSchedule: RestrictedRoute(
        "/create-schedule",
        () => <CreateSchedule />,
        "schedules",
        "administrator",
    ),
    createScheduleById: RestrictedRoute<{ id: string }>(
        path`/create-schedule/${uuid(":id")}`,
        ({ id }) => <CreateSchedule deviceId={id} />,
        "schedules",
        "administrator",
    ),
    cloneRole: RestrictedRoute<{ id: string }>(
        path`/clone/${uuid(":id")}`,
        CloneRole,
        "roles",
        "administrator",
    ),
    createRole: RestrictedRoute(
        "/create-role",
        () => <CreateRole />,
        "users",
        "administrator",
    ),
    createServiceAccount: RestrictedRoute(
        "/create-service-account",
        () => <CreateServiceAccount />,
        "organization",
        "administrator",
    ),
    createPersona: RestrictedRoute(
        "/create-persona",
        () => <CreatePersona />,
        "organization",
        "administrator",
    ),
    editUser: RestrictedRoute<{ id: string }>(
        path`/users/${uuid(":id")}`,
        EditUserById,
        "users",
        "administrator",
    ),
    editTeam: RestrictedRoute<{ id: string }>(
        path`/teams/${uuid(":id")}`,
        EditTeamById,
        "users",
        "administrator",
    ),
    editRole: RestrictedRoute<{ id: string }>(
        path`/roles/${uuid(":id")}`,
        EditRoleById,
        "users",
        "administrator",
    ),
    editSchedule: RestrictedRoute<{ id: string }>(
        path`/schedules/${uuid(":id")}`,
        EditScheduleById,
        "schedules",
        "administrator",
    ),
    bulkProvision: RestrictedRoute(
        "/bulk-provision",
        () => <BulkProvision />,
        "devices",
        "administrator",
    ),
    createChannel: RestrictedRoute(
        "/create-channel",
        () => <CreateChannel />,
        "channels",
        "administrator",
    ),
    channel: DefaultRoute<{ id: string }>(
        path`/${uuid(":id")}`,
        ({ id }) => <ChannelById key={id} id={id} />,
        "observability",
    ),
    editChannel: RestrictedRoute<{ id: string }>(
        path`/${uuid(":id")}/edit`,
        EditChannelById,
        "channels",
        "administrator",
    ),
    channelEvents: DefaultRoute<{ id: string }>(
        path`/${uuid(":id")}/events`,
        ({ id }) => <Events channelId={id} />,
        "events",
    ),
    // TODO: Remove (backwards-compatibility)
    channelEvent: DefaultRoute<{ id: string; eventId: string }>(
        path`/${uuid(":id")}/events/${uuid(":eventId")}`,
        ({ id, eventId }) => <ChannelEvent id={id} eventId={eventId} />,
        "events",
    ),
    createGroup: RestrictedRoute(
        "/group/new",
        () => <CreateGroup />,
        "devices",
        "administrator",
    ),
    editGroup: RestrictedRoute<{ id: string }>(
        path`/group/${uuid(":id")}/edit`,
        EditGroupById,
        "devices",
        "administrator",
    ),
    createTagTemplate: RestrictedRoute(
        "/tag/new",
        () => <CreateTagTemplate />,
        "devices",
        "administrator",
    ),
    editTagTemplate: RestrictedRoute<{ id: string }>(
        path`/tag/${uuid(":id")}/edit`,
        EditTagTemplateById,
        "devices",
        "administrator",
    ),
    createStream: RestrictedRoute(
        "/stream/new",
        () => <CreateStream />,
        "streams",
        "administrator",
    ),
    editStream: RestrictedRoute<{ id: string }>(
        path`/stream/${uuid(":id")}/edit`,
        EditStreamById,
        "streams",
        "administrator",
    ),
    createAdapter: RestrictedRoute(
        "/adapter/new",
        () => <CreateAdapter />,
        "integrations",
        "administrator",
    ),
    createCustomModule: RestrictedRoute(
        "/module/new",
        () => <CreateModule />,
        "integrations",
        "administrator",
    ),
    editAdapter: RestrictedRoute<{ id: string }>(
        path`/adapter/${uuid(":id")}/edit`,
        EditAdapterById,
        "integrations",
        "administrator",
    ),
    editCustomModule: RestrictedRoute<{ id: string }>(
        path`/module/${uuid(":id")}/edit`,
        EditModuleById,
        "integrations",
        "administrator",
    ),
};
